export const APPROVER_DISPLAY: Record<string, string> = {
  VM: "Vendor Manager",
  CM: "Category Manager",
  COMPLIANCE: "Compliance",
  FINANCE: "Finance",
  PROCUREMENT: "Procurement",
  VM_COMPLIANCE: "Compliance",
  VM_REVIEW: "Vendor Manager",
  CM_REVIEW: "Category Manager",
  FINANCE_REVIEW: "Finance",
  COMPLIANCE_REVIEW: "Compliance",
};

export function approverDisplayName(role: string | undefined): string {
  if (!role) return "reviewer";
  return APPROVER_DISPLAY[role] ?? "reviewer";
}

export const FIELD_LABELS: Record<string, string> = {
  email_id: "Primary Email ID",
  poc_contact: "POC Number",
  poc_contact_country_code: "POC Country Code",
  po_notification_emails: "PO Notification Emails",
  escalation_emails: "Escalation Emails",
  vendor_invoice_portal_access_email: "Vendor Invoice Portal Access",
  tars_production_process_management_access_email:
    "TARs Production Process Access",
  address_billing: "Billing Address",
  location_pincode: "Pincode",
  billing_state: "Billing State",
  billing_city: "Billing City",
  billing_country: "Billing Country",
  account_number: "Bank Account Number",
  ifsc_code: "IFSC Code",
  bank_name: "Bank Name",
  branch_name: "Bank Branch",
  account_name: "Account Holder Name",
  account_type: "Account Type",
  bank_verification_document: "Cancelled Cheque",
  msme_certificate: "MSME Certificate",
  msme_number: "MSME Number",
  msme_validity: "MSME Validity",
  msme_registration_date: "MSME Date of Registration",
  vendor_type: "Vendor Type",
  legal_name: "Vendor Legal Name",
  pan_number: "PAN Number",
  pan_card_attachment: "PAN Card",
  gst: "GST Number",
  gst_certificate: "GST Certificate",
  vendor_profile_type: "Vendor Profile Type",
  super_category: "Super Category",
  sub_category: "Sub Category",
  credit_days: "Credit Days",
  payment_terms_pattern: "Payment Terms Pattern",
  payment_terms_remarks: "Payment Terms Remarks",
  advance_applicable: "Advance Applicable",
  advance_percentage: "Advance Percentage",
};

function unwrapSingleKey(value: unknown): unknown {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const keys = Object.keys(value as Record<string, unknown>);
    if (keys.length === 1) return (value as Record<string, unknown>)[keys[0]];
  }
  return value;
}

function isBlank(value: unknown): boolean {
  return value == null || value === "";
}

export function stringifyValue(raw: unknown): string {
  const value = unwrapSingleKey(raw);
  if (isBlank(value)) return "—";
  if (Array.isArray(value)) {
    const items = value
      .filter((item) => !isBlank(unwrapSingleKey(item)))
      .map(stringifyValue);
    return items.length > 0 ? items.join(", ") : "—";
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export const DOCUMENT_FIELDS = new Set<string>([
  "msme_certificate",
  "gst_certificate",
  "bank_verification_document",
  "pan_card_attachment",
]);

export function isDocumentField(field: string): boolean {
  return DOCUMENT_FIELDS.has(field);
}

export type FieldApproverRole =
  | "VM"
  | "COMPLIANCE"
  | "FINANCE"
  | "PROCUREMENT"
  | "CM";

export const FIELD_APPROVER_ROLES: Record<string, FieldApproverRole | null> = {
  email_id: "VM",
  vendor_invoice_portal_access_email: "VM",
  tars_production_process_management_access_email: "VM",
  poc_contact: "VM",
  poc_contact_country_code: "VM",
  msme_certificate: "VM",
  msme_number: "VM",
  msme_registration_date: "VM",
  address_billing: "VM",
  address_billing_line_1: "VM",
  address_billing_line_2: "VM",
  billing_state: "VM",
  billing_city: "VM",
  billing_country: null,
  location_pincode: "VM",
  account_number: "FINANCE",
  ifsc_code: "FINANCE",
  bank_name: "FINANCE",
  branch_name: "FINANCE",
  account_name: "FINANCE",
  account_type: "FINANCE",
  bank_verification_document: "FINANCE",
  credit_days: "PROCUREMENT",
  payment_terms_pattern: "PROCUREMENT",
  vendor_type: "COMPLIANCE",
  gst: "COMPLIANCE",
  gst_certificate: "COMPLIANCE",
  pan_number: "COMPLIANCE",
  legal_name: "COMPLIANCE",
  pan_card_attachment: "COMPLIANCE",
  vendor_profile_type: "CM",
  super_category: "CM",
  sub_category: "CM",
  po_notification_emails: null,
  escalation_emails: null,
  advance_applicable: null,
  advance_percentage: null,
};

export type ReviewStage =
  | "VM_REVIEW"
  | "CM_REVIEW"
  | "FINANCE_REVIEW"
  | "COMPLIANCE_REVIEW";

function stagesForRole(role: FieldApproverRole | null | undefined): ReviewStage[] {
  switch (role) {
    case "VM":
      return ["VM_REVIEW"];
    case "CM":
      return ["CM_REVIEW"];
    case "COMPLIANCE":
      return ["COMPLIANCE_REVIEW"];
    case "FINANCE":
      return ["FINANCE_REVIEW"];
    case "PROCUREMENT":
      return ["CM_REVIEW", "FINANCE_REVIEW"];
    default:
      return [];
  }
}

export function requiredReviewStages(fields: string[]): Set<ReviewStage> {
  const need = new Set<ReviewStage>();
  for (const f of fields) {
    const inMap = Object.prototype.hasOwnProperty.call(FIELD_APPROVER_ROLES, f);
    const role = FIELD_APPROVER_ROLES[f];
    if (!inMap && process.env.NODE_ENV !== "production") {
      console.warn(
        `[reviewStages] Field "${f}" is not in FIELD_APPROVER_ROLES — reviewer pickers may be wrong. Update the map when BE adds a new gated field.`,
      );
    }
    for (const s of stagesForRole(role)) need.add(s);
  }
  if (need.has("CM_REVIEW") || need.has("FINANCE_REVIEW")) {
    need.add("VM_REVIEW");
  }
  return need;
}
